/**
 * BugReportOverlay — Overlay flottant de signalement de bug.
 *
 * • Bouton flottant discret en bas-droit (data-testid="bug-report-btn").
 * • Modal : description + sévérité + capture d'écran auto (html2canvas)
 *   + contexte technique auto-collecté (viewport, device, user-agent,
 *   langue, fuseau, connectivité, build hash, erreurs console récentes).
 * • Envoi POST /api/bug-report — endpoint public, aucune auth requise.
 * • Copier presse-papier + Ouvrir email pré-rempli en fallback.
 *
 * Le composant est monté globalement dans App.js. Aucune dépendance à
 * AuthProvider — il fonctionne aussi sur les pages publiques /site/*.
 */
import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Bug, Camera, Send, X, Copy, CheckCircle2, Mail } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BUILD_HASH = process.env.REACT_APP_BUILD_HASH || "dev";
const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || "";

// Ring buffer pour les erreurs console (dernières 20)
const CONSOLE_BUFFER = [];
if (typeof window !== "undefined" && !window.__eliaBugReporterInstalled) {
  window.__eliaBugReporterInstalled = true;
  const origError = console.error;
  const origWarn = console.warn;
  console.error = (...args) => {
    try {
      CONSOLE_BUFFER.push(`[ERR] ${args.map((a) => String(a)).join(" ").slice(0, 500)}`);
      if (CONSOLE_BUFFER.length > 20) CONSOLE_BUFFER.shift();
    } catch (_) { /* ignore */ }
    return origError.apply(console, args);
  };
  console.warn = (...args) => {
    try {
      CONSOLE_BUFFER.push(`[WARN] ${args.map((a) => String(a)).join(" ").slice(0, 500)}`);
      if (CONSOLE_BUFFER.length > 20) CONSOLE_BUFFER.shift();
    } catch (_) { /* ignore */ }
    return origWarn.apply(console, args);
  };
  window.addEventListener("error", (e) => {
    CONSOLE_BUFFER.push(`[UNCAUGHT] ${e.message} @ ${e.filename}:${e.lineno}`);
    if (CONSOLE_BUFFER.length > 20) CONSOLE_BUFFER.shift();
  });
}

function collectContext() {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const win = typeof window !== "undefined" ? window : {};
  return {
    url: win.location?.href || "",
    viewport: `${win.innerWidth || 0}x${win.innerHeight || 0}`,
    device_pixel_ratio: win.devicePixelRatio || 1,
    user_agent: nav.userAgent || "",
    language: nav.language || "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    online: nav.onLine !== false,
    connection: nav.connection?.effectiveType || "",
    build_hash: BUILD_HASH,
    build_time: BUILD_TIME,
    console_errors: [...CONSOLE_BUFFER],
  };
}

export default function BugReportOverlay() {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [reporterEmail, setReporterEmail] = useState("");
  const modalRef = useRef(null);
  const context = useRef(collectContext());

  useEffect(() => {
    if (open) {
      context.current = collectContext();
    }
  }, [open]);

  async function captureScreenshot() {
    setCapturing(true);
    try {
      // On masque le modal pendant la capture pour éviter de le voir dedans
      const modal = modalRef.current;
      if (modal) modal.style.visibility = "hidden";
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: Math.min(1, window.devicePixelRatio || 1),
        logging: false,
        // Reduce size for reasonable payload
        windowWidth: document.body.scrollWidth,
        windowHeight: window.innerHeight,
      });
      if (modal) modal.style.visibility = "visible";
      // JPEG à qualité 60 pour rester sous 2 Mo
      const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
      setScreenshot(dataUrl);
    } catch (e) {
      console.warn("html2canvas failed", e);
      setScreenshot(null);
    } finally {
      setCapturing(false);
    }
  }

  function reset() {
    setDescription("");
    setSeverity("medium");
    setScreenshot(null);
    setStatus(null);
    setReporterEmail("");
  }

  async function submit() {
    if (description.trim().length < 3) return;
    setSending(true);
    try {
      const payload = {
        description: description.trim(),
        severity,
        screenshot_data_url: screenshot || "",
        reporter_email: reporterEmail,
        ...context.current,
      };
      const res = await axios.post(`${BACKEND_URL}/api/bug-report`, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 20000,
      });
      setStatus({ type: "ok", data: res.data });
    } catch (e) {
      setStatus({ type: "err", error: e?.response?.data?.detail || "Envoi impossible. Copiez le contexte ci-dessous et envoyez-le par email." });
    } finally {
      setSending(false);
    }
  }

  async function copyContext() {
    const summary = {
      description,
      severity,
      ...context.current,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(summary, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* ignore */ }
  }

  function openMail() {
    const body = encodeURIComponent(
      `Description :\n${description}\n\n---\nContexte technique :\n${JSON.stringify(context.current, null, 2)}`
    );
    const subject = encodeURIComponent(`[BUG ELIA] ${description.slice(0, 60)}`);
    window.open(`mailto:founder@elia-finance.com?subject=${subject}&body=${body}`, "_blank");
  }

  return (
    <>
      {/* Bouton flottant */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Signaler un bug"
        title="Signaler un bug (Ctrl+Shift+B)"
        data-testid="bug-report-btn"
        className="fixed z-[100] bottom-5 left-5 w-11 h-11 rounded-full bg-slate-800/85 hover:bg-slate-900 text-white shadow-lg hover:shadow-xl transition-all backdrop-blur-md flex items-center justify-center group"
      >
        <Bug className="w-4 h-4 group-hover:scale-110 transition-transform" />
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="bug-report-modal"
        >
          <div
            ref={modalRef}
            className="bg-background rounded-2xl border border-border/60 shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Bug className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-medium">Signaler un problème</h3>
                  <p className="text-xs text-muted-foreground">Nous répondons sous 48h ouvrées.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg hover:bg-elevated"
                aria-label="Fermer"
                data-testid="bug-report-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            {status?.type === "ok" ? (
              <div className="p-8 text-center" data-testid="bug-report-success">
                <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
                <h4 className="mt-4 font-display text-xl font-medium">Rapport envoyé</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  {status.data?.message || "Merci — nous étudions votre signalement."}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  ID : <code className="text-primary">{status.data?.id?.slice(0, 8)}</code>
                </p>
                <button
                  type="button"
                  onClick={() => { reset(); setOpen(false); }}
                  className="mt-6 inline-flex items-center h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-dark"
                  data-testid="bug-report-close-success"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                {/* Description */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Description du problème *
                  </label>
                  <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Que faisiez-vous ? Que s'est-il passé ? Que deviez-vous voir ?"
                    className="mt-1.5 w-full rounded-lg border border-border/70 bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    data-testid="bug-report-description"
                  />
                </div>

                {/* Sévérité */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Sévérité
                  </label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {[
                      { key: "low", label: "Mineur", color: "bg-slate-100 text-slate-800" },
                      { key: "medium", label: "Moyen", color: "bg-sky-100 text-sky-800" },
                      { key: "high", label: "Important", color: "bg-amber-100 text-amber-800" },
                      { key: "critical", label: "Bloquant", color: "bg-rose-100 text-rose-800" },
                    ].map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setSeverity(s.key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          severity === s.key
                            ? `${s.color} ring-2 ring-primary/40`
                            : "bg-elevated text-ink-soft/70 hover:bg-primary/10"
                        }`}
                        data-testid={`bug-severity-${s.key}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Email contact (optional) */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Email de contact (optionnel)
                  </label>
                  <input
                    type="email"
                    value={reporterEmail}
                    onChange={(e) => setReporterEmail(e.target.value)}
                    placeholder="vous@example.com"
                    className="mt-1.5 w-full rounded-lg border border-border/70 bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    data-testid="bug-report-email"
                  />
                </div>

                {/* Screenshot */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Capture d&apos;écran
                    </label>
                    <button
                      type="button"
                      onClick={captureScreenshot}
                      disabled={capturing}
                      className="text-xs text-primary font-medium hover:underline disabled:opacity-60 flex items-center gap-1"
                      data-testid="bug-report-capture"
                    >
                      <Camera className="w-3.5 h-3.5" />
                      {capturing ? "Capture…" : screenshot ? "Recapturer" : "Capturer maintenant"}
                    </button>
                  </div>
                  {screenshot ? (
                    <div className="rounded-lg border border-border/60 overflow-hidden">
                      <img src={screenshot} alt="Capture" className="w-full max-h-64 object-contain bg-elevated" />
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
                      Cliquez « Capturer maintenant » pour joindre une capture automatique.
                    </div>
                  )}
                </div>

                {/* Contexte auto */}
                <details className="rounded-lg border border-border/50 p-3" data-testid="bug-context-details">
                  <summary className="cursor-pointer text-xs uppercase tracking-widest text-muted-foreground">
                    Contexte technique auto-collecté
                  </summary>
                  <pre className="mt-3 text-[10px] leading-relaxed text-ink-soft/85 whitespace-pre-wrap max-h-40 overflow-y-auto">
{JSON.stringify(context.current, null, 2)}
                  </pre>
                </details>

                {status?.type === "err" && (
                  <div className="text-sm text-rose-600" data-testid="bug-report-error">
                    {status.error}
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={sending || description.trim().length < 3}
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-dark disabled:opacity-60 transition-colors"
                    data-testid="bug-report-submit"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? "Envoi…" : "Envoyer"}
                  </button>
                  <button
                    type="button"
                    onClick={copyContext}
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-border/70 text-sm font-medium hover:border-primary/60"
                    data-testid="bug-report-copy"
                  >
                    {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copié" : "Copier"}
                  </button>
                  <button
                    type="button"
                    onClick={openMail}
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-border/70 text-sm font-medium hover:border-primary/60"
                    data-testid="bug-report-mail"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
